import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Skeleton } from 'primereact/skeleton';
import { url } from '../../../../env';
import { AppButton, AppIconButton } from '../../../../Components/FormKit';
import usePermissions from '../../../../Hooks/usePermissions';
import { usersManagementCSS, roleChipCSS, stateChipCSS } from './UsersManagement.css';
import { ROLE_KPI_DEFS, TRIM_STATE_KPI_DEFS, labelOf, formatDate } from './roleConfig';
import UserDetailModal from './UserDetailModal';
import CreateUserModal from './CreateUserModal';

const KpiCardsSkeleton = ({ adminPei }) => {
    const count = adminPei ? ROLE_KPI_DEFS.length : TRIM_STATE_KPI_DEFS.length;
    return (
        <div className={`um-kpis${adminPei ? '' : ' um-kpis--trim'}`}>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="um-kpi um-kpi-skeleton" aria-busy="true">
                    <Skeleton shape="circle" size="36px" borderRadius="10px" />
                    <span className="um-kpi-body">
                        <Skeleton width="55%" height="0.62rem" borderRadius="4px" />
                        <Skeleton width="40%" height="1.5rem" borderRadius="4px" />
                    </span>
                </div>
            ))}
        </div>
    );
};

const TableRowsSkeleton = ({ rows = 8 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <tr key={`skel-${i}`} className="um-row-skeleton" aria-busy="true">
                <td><Skeleton width="24px" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="70%" height="0.85rem" borderRadius="4px" /></td>
                <td><Skeleton width="85%" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="72px" height="18px" borderRadius="999px" /></td>
                <td><Skeleton width="62px" height="18px" borderRadius="999px" /></td>
                <td><Skeleton width="78px" height="0.78rem" borderRadius="4px" /></td>
                <td style={{ textAlign: 'right' }}>
                    <Skeleton shape="circle" size="20px" />
                </td>
            </tr>
        ))}
    </>
);
const PER_PAGE = 15;

const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const sevenDaysAgoISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString();
};

const UsersManagement = ({ toast }) => {
    const { isAdminPei } = usePermissions();
    const adminPei = isAdminPei();

    // Admin Pei filtra por rol; Admin Trim filtra por estado/recencia.
    // En ambos casos, `null` = sin filtro adicional (chip "Todos" / "Total").
    const [roleFilter, setRoleFilter] = useState(null);          // string | 'null' | null
    const [stateFilter, setStateFilter] = useState(null);        // 'active' | 'inactive' | 'new_week' | null

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);

    const [users, setUsers] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1, kpis: {} });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedUser, setSelectedUser] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);

    // ── Debounce search ──
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(id);
    }, [search]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [roleFilter, stateFilter, debouncedSearch]);

    const fetchUsers = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();

            if (adminPei && roleFilter !== null && roleFilter !== undefined) {
                params.append('role', roleFilter);
            }

            if (!adminPei) {
                if (stateFilter === 'active')   params.append('active', '1');
                if (stateFilter === 'inactive') params.append('active', '0');
                if (stateFilter === 'new_week') params.append('created_after', sevenDaysAgoISO());
            }

            if (debouncedSearch) params.append('search', debouncedSearch);
            params.append('page', String(page));

            const res = await axios.get(`${url}/users?${params.toString()}`, {
                ...authHeader(),
                signal,
            });
            setUsers(res.data?.data ?? []);
            setMeta(res.data?.meta ?? { total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1, kpis: {} });
        } catch (e) {
            if (axios.isCancel(e)) return;
            console.error(e);
            setError('No se pudieron cargar los usuarios.');
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron cargar los usuarios.',
                life: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, [adminPei, roleFilter, stateFilter, debouncedSearch, page, toast]);

    useEffect(() => {
        const controller = new AbortController();
        fetchUsers(controller.signal);
        return () => controller.abort();
    }, [fetchUsers]);

    const openUser = (u) => {
        setSelectedUser(u);
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedUser(null);
    };

    const onUserUpdated = () => {
        fetchUsers();
    };

    const goPrev = () => page > 1 && setPage(page - 1);
    const goNext = () => page < meta.last_page && setPage(page + 1);

    const onCreateUser = () => setCreateModalVisible(true);
    const onUserCreated = () => fetchUsers();

    const startItem = meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0;
    const endItem = Math.min(meta.current_page * meta.per_page, meta.total);

    const kpis = meta.kpis ?? {};

    return (
        <div css={usersManagementCSS()} className="fk-root">
            <header className="um-header">
                <div>
                    <h1 className="um-title">Solo Usuarios</h1>
                    <p className="um-subtitle">
                        {adminPei
                            ? 'Administra todos los usuarios de la plataforma.'
                            : 'Administra todos los Vendedores Trim del equipo.'}
                    </p>
                </div>

                <AppButton
                    label={adminPei ? '+ Crear usuario' : '+ Invitar vendedor'}
                    variant="primary"
                    size="md"
                    onClick={onCreateUser}
                />
            </header>

            {loading ? (
                <KpiCardsSkeleton adminPei={adminPei} />
            ) : adminPei ? (
                <div className="um-kpis">
                    {ROLE_KPI_DEFS.map((def) => {
                        const isActive = (def.role ?? null) === (roleFilter ?? null);
                        const value = kpis[def.key] ?? 0;
                        return (
                            <button
                                type="button"
                                key={def.key}
                                className={`um-kpi tone-${def.tone} ${isActive ? 'active' : ''}`}
                                onClick={() => setRoleFilter(def.role ?? null)}
                                aria-pressed={isActive}
                            >
                                <span className="um-kpi-icon"><i className={def.icon} /></span>
                                <span className="um-kpi-body">
                                    <span className="um-kpi-label">{def.label}</span>
                                    <span className="um-kpi-value">{value}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="um-kpis um-kpis--trim">
                    {TRIM_STATE_KPI_DEFS.map((def) => {
                        const isActive =
                            (def.key === 'total' && stateFilter === null) ||
                            (def.key !== 'total' && stateFilter === def.key);
                        const value = kpis[def.key] ?? 0;
                        return (
                            <button
                                type="button"
                                key={def.key}
                                className={`um-kpi tone-${def.tone} ${isActive ? 'active' : ''}`}
                                onClick={() => setStateFilter(def.key === 'total' ? null : def.key)}
                                aria-pressed={isActive}
                            >
                                <span className="um-kpi-icon"><i className={def.icon} /></span>
                                <span className="um-kpi-body">
                                    <span className="um-kpi-label">{def.label}</span>
                                    <span className="um-kpi-value">{value}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="um-toolbar">
                <div className="um-search">
                    <span className="um-search-icon">
                        <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'} />
                    </span>
                    <input
                        type="text"
                        placeholder="Buscar por nombre o email"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="um-table-card">
                <div className="um-table-scroll">
                    <table className="um-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Email</th>
                                <th>Rol</th>
                                <th>Estado</th>
                                <th>Fecha alta</th>
                                <th style={{ textAlign: 'right' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <TableRowsSkeleton rows={8} />}

                            {!loading && error && (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="um-empty">
                                            <i className="pi pi-exclamation-triangle" style={{ color: '#ef4444' }} />
                                            <span>{error}</span>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!loading && !error && users.length === 0 && (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="um-empty">
                                            <i className="pi pi-users" />
                                            <span>No se encontraron usuarios</span>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!loading && !error && users.map((u) => (
                                <tr key={u.id} onClick={() => openUser(u)}>
                                    <td>{u.id}</td>
                                    <td>{u.name || '—'}</td>
                                    <td>{u.email || '—'}</td>
                                    <td>
                                        <span css={roleChipCSS(u.type_user)}>
                                            {labelOf(u.type_user)}
                                        </span>
                                    </td>
                                    <td>
                                        <span css={stateChipCSS(!!u.is_active)}>
                                            {u.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td>{formatDate(u.created_at)}</td>
                                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                        <AppIconButton
                                            icon="pi pi-pencil"
                                            title="Editar"
                                            onClick={() => openUser(u)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!loading && !error && users.length > 0 && (
                    <div className="um-pagination">
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Mostrando <strong style={{ color: '#0f172a' }}>{startItem}</strong> a{' '}
                            <strong style={{ color: '#0f172a' }}>{endItem}</strong> de{' '}
                            <strong style={{ color: '#0f172a' }}>{meta.total}</strong> resultados
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <AppIconButton
                                icon="pi pi-chevron-left"
                                onClick={goPrev}
                                disabled={page <= 1}
                                title="Anterior"
                            />
                            <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                                {meta.current_page} / {meta.last_page}
                            </span>
                            <AppIconButton
                                icon="pi pi-chevron-right"
                                onClick={goNext}
                                disabled={page >= meta.last_page}
                                title="Siguiente"
                            />
                        </div>
                    </div>
                )}
            </div>

            <UserDetailModal
                user={selectedUser}
                visible={modalVisible}
                onHide={closeModal}
                onUpdate={onUserUpdated}
                toast={toast}
            />

            <CreateUserModal
                visible={createModalVisible}
                onHide={() => setCreateModalVisible(false)}
                onCreated={onUserCreated}
                toast={toast}
            />
        </div>
    );
};

export default UsersManagement;
